import { createSlice } from "@reduxjs/toolkit";
import {
  getBandsBuilder,
  getAdminBandsBuilder,
  getBandByIdBuilder,
  getBandsForDropdownBuilder,
  getFeaturedBandsBuilder,
  createBandBuilder,
  editBandBuilder,
  deleteBandBuilder,
  toggleBandStatusBuilder,
  getTopBandsByRevenueBuilder,
  getBandAnalyticsBuilder,
  getBandProductAnalyticsBuilder,
  getProductsByBandBuilder,
  createDashboardAccessBuilder,
  resetDashboardPasswordBuilder,
  removeDashboardAccessBuilder,
  getBandDetailsBuilder,
  updateBandDetailsBuilder,
  getBandPublicDetailsBuilder,
  getBandByNameBuilder,
} from "./builder";
import { fetchAdminCredentials } from "./asyncThunks";

const initialState = {
  bands: {
    data: [],
    maxReached: false,
    pagination: {
      currentPage: 1,
      hasNextPage: false,
      hasPreviousPage: false,
      limit: 10,
      totalBands: 0,
      totalPages: 0,
    },
    loading: false,
    createLoading: false,
    editLoading: false,
    deleteLoading: false,
    toggleLoading: false,
    error: null,
  },

  bandById: {
    data: null,
    loading: false,
    error: null,
  },

  bandsDropdown: {
    data: [],
    loading: false,
    error: null,
  },

  featuredBands: {
    data: [],
    loading: false,
    error: null,
  },

  bandsByRevenue: {
    data: [],
    loading: false,
    error: null,
  },

  // DASHBOARD PRISTUP
  dashboardAccess: {
    createLoading: false,
    resetLoading: false,
    removeLoading: false,
    error: null,
    lastCreated: null,
    lastReset: null,
    lastRemoved: null,
  },

  // Analytics states
  bandAnalytics: {
    data: null,
    loading: false,
    error: null,
  },

  bandProductAnalytics: {
    data: null,
    loading: false,
    error: null,
  },

  bandProducts: {
    data: [],
    total: 0,
    page: 1,
    perPage: 12,
    maxReached: false,
    band: null,
    loading: false,
    error: null,
  },

  adminCredentials: {
    data: {},
    loading: false,
    error: null,
  },

  bandDetails: {
    data: null,
    loading: false,
    updateLoading: false,
    error: null,
  },

  publicBandDetails: {
    data: null,
    loading: false,
    error: null,
  },

  bandByName: {
    data: null,
    loading: false,
    error: null,
  },
};

export const bandsSlice = createSlice({
  name: "bandsInfo",
  initialState,
  reducers: {
    clearBandById: (state) => {
      state.bandById.data = null;
      state.bandById.error = null;
    },
    clearBandsError: (state) => {
      state.bands.error = null;
    },
    clearFeaturedBandsError: (state) => {
      state.featuredBands.error = null;
    },
    resetBandsState: (state) => {
      state.bands = initialState.bands;
      state.bandById = initialState.bandById;
      state.bandsDropdown = initialState.bandsDropdown;
    },
    clearBandAnalytics: (state) => {
      state.bandAnalytics.data = null;
      state.bandAnalytics.error = null;
    },
    clearBandProductAnalytics: (state) => {
      state.bandProductAnalytics.data = null;
      state.bandProductAnalytics.error = null;
    },
    clearBandProducts: (state) => {
      state.bandProducts.data = [];
      state.bandProducts.total = 0;
      state.bandProducts.page = 1;
      state.bandProducts.band = null;
      state.bandProducts.error = null;
    },

    reset: (state) => {
      state.adminCredentials = {
        data: {},
        loading: false,
        error: null,
      };
    },

    // ACTIONS ZA DASHBOARD PRISTUP
    clearDashboardAccessError: (state) => {
      state.dashboardAccess.error = null;
    },
    clearDashboardAccessResults: (state) => {
      state.dashboardAccess.lastCreated = null;
      state.dashboardAccess.lastReset = null;
      state.dashboardAccess.lastRemoved = null;
    },
    resetDashboardAccessState: (state) => {
      state.dashboardAccess = initialState.dashboardAccess;
    },

    clearBandDetails: (state) => {
      state.bandDetails.data = null;
      state.bandDetails.error = null;
    },

    clearPublicBandDetails: (state) => {
      state.publicBandDetails.data = null;
      state.publicBandDetails.error = null;
    },

    clearBandByName: (state) => {
      state.bandByName.data = null;
      state.bandByName.error = null;
    },

    resetBandDetailsState: (state) => {
      state.bandDetails = {
        data: null,
        loading: false,
        updateLoading: false,
        error: null,
      };
      state.publicBandDetails = {
        data: null,
        loading: false,
        error: null,
      };
      state.bandByName = {
        data: null,
        loading: false,
        error: null,
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminCredentials.pending, (state) => {
        state.adminCredentials.loading = true;
      })
      .addCase(fetchAdminCredentials.fulfilled, (state, action) => {
        state.adminCredentials.loading = false;
        const { bandId, adminCredentials } = action.payload;
        state.adminCredentials.data[bandId] = adminCredentials;
      })
      .addCase(fetchAdminCredentials.rejected, (state, action) => {
        state.adminCredentials.loading = false;
        state.adminCredentials.error = true;
      });

    getBandsBuilder(builder);
    getAdminBandsBuilder(builder);
    getBandByIdBuilder(builder);
    getBandsForDropdownBuilder(builder);
    getFeaturedBandsBuilder(builder);
    createBandBuilder(builder);
    editBandBuilder(builder);
    deleteBandBuilder(builder);
    toggleBandStatusBuilder(builder);
    getTopBandsByRevenueBuilder(builder);

    // Analytics builders
    getBandAnalyticsBuilder(builder);
    getBandProductAnalyticsBuilder(builder);
    getProductsByBandBuilder(builder);

    // BUILDER-I ZA DASHBOARD PRISTUP
    createDashboardAccessBuilder(builder);
    resetDashboardPasswordBuilder(builder);
    removeDashboardAccessBuilder(builder);

    getBandDetailsBuilder(builder);
    updateBandDetailsBuilder(builder);
    getBandPublicDetailsBuilder(builder);
    getBandByNameBuilder(builder);

    // Handle logout - clear all data
    builder.addCase("auth/logout", (state) => {
      state.bands = initialState.bands;
      state.bandById = initialState.bandById;
      state.bandsDropdown = initialState.bandsDropdown;
      state.dashboardAccess = initialState.dashboardAccess;
      state.bandAnalytics = initialState.bandAnalytics;
      state.bandProductAnalytics = initialState.bandProductAnalytics;
      state.bandProducts = initialState.bandProducts;
    });
  },
});

// Export synchronous actions
export const {
  clearBandById,
  clearBandsError,
  clearFeaturedBandsError,
  resetBandsState,
  clearBandAnalytics,
  clearBandProductAnalytics,
  clearBandProducts,
  reset,
  clearDashboardAccessError,
  clearDashboardAccessResults,
  clearBandDetails,
  clearPublicBandDetails,
  clearBandByName,
  resetBandDetailsState,
  resetDashboardAccessState,
} = bandsSlice.actions;

export const selectBands = (state) => state.bandsInfo.bands;
export const selectAdminCredentials = (state) =>
  state.bandsInfo.adminCredentials;
export const selectBandById = (state) => state.bandsInfo.bandById;
export const selectBandsDropdown = (state) => state.bandsInfo.bandsDropdown;
export const selectFeaturedBands = (state) => state.bandsInfo.featuredBands;
export const selectBandsByRevenue = (state) => state.bandsInfo.bandsByRevenue;
export const selectBandAnalytics = (state) => state.bandsInfo.bandAnalytics;
export const selectBandProductAnalytics = (state) =>
  state.bandsInfo.bandProductAnalytics;
export const selectBandProducts = (state) => state.bandsInfo.bandProducts;

// DASHBOARD PRISTUP
export const selectDashboardAccess = (state) => state.bandsInfo.dashboardAccess;

// Advanced selectors
export const selectActiveBands = (state) => ({
  ...state.bandsInfo.bands,
  data: state.bandsInfo.bands.data.filter((band) => band.active),
});

export const selectFeaturedBandsFromAll = (state) => ({
  ...state.bandsInfo.bands,
  data: state.bandsInfo.bands.data.filter((band) => band.isFeatured),
});

//  ADVANCED SELECTOR - Bendovi sa/bez dashboard pristupa
export const selectBandsWithDashboardAccess = (state) => ({
  ...state.bandsInfo.bands,
  data: state.bandsInfo.bands.data.filter((band) => band.adminCredentials),
});

export const selectBandsWithoutDashboardAccess = (state) => ({
  ...state.bandsInfo.bands,
  data: state.bandsInfo.bands.data.filter((band) => !band.adminCredentials),
});

export const selectBandDetails = (state) => state.bandsInfo.bandDetails;
export const selectPublicBandDetails = (state) =>
  state.bandsInfo.publicBandDetails;
export const selectBandByName = (state) => state.bandsInfo.bandByName;

export default bandsSlice.reducer;
